// In all this functions, the metadata is assumed to have the format like below:
// { securityQuestion1: {valid: false, message: "Security question 1 is required", dirty: true}}
// 3 fields are expected in the metadata object for each property:
//      valid: Indicator that the field is valid or not
//      message: Error message if the field is not valid
//      dirty: Indicator that the field has been "touched" or not (usually when user blur the field, it should marked as dirty)

/**
 * @Deprecated Use getFormInputCssClass instead
 * Get input error class (has-error) if the condition is satisfied. Only return has error is the property is not valid
 * and dirty.
 *
 * @param metadata The metadata containing the property information
 * @param propertyName The property to be checked
 * @returns {string} "has-error" when the condition above is satisfied.
 */
export const getInputErrorClass = (metadata, propertyName) => {
    if (metadata[propertyName]) {
        const {dirty, valid} = metadata[propertyName];

        if (valid || !dirty) {
            return "";
        }
        return " has-error";
    } else {
        return "";
    }
};

/**
 * Get common UI class for fields
 * @param metadata
 * @param propertyName
 * @returns {string}
 */
export const getFormInputCssClass = (metadata, propertyName) => {
    if (metadata[propertyName]) {
        const {dirty, valid} = metadata[propertyName];
        if (dirty){
            if (valid){
                return " is-valid";
            } else {
                return " has-error";
            }
        } else {
            return "";
        }
    } else {
        return "";
    }
};

/**
 * @Deprecated Use getFormInputCssClass instead
 * Get input valid class (is-valid) if the condition is satisfied.
 * Only return has is-valid if the property is valid and dirty.
 *
 * @param metadata The metadata containing the property information
 * @param propertyName The property to be checked
 * @returns {string} "is-valid" when the condition above is satisfied.
 */
export const getInputValidClass = (metadata, propertyName) => {
    if (metadata[propertyName]) {
        const {dirty, valid} = metadata[propertyName];
        if (valid && dirty) {
            return " is-valid";
        }
    }
    return "";
};

/**
 * Return true if the property message is specified and the property is dirty and not valid.
 *
 * @param metadata The metadata
 * @param propertyName The property name
 * @returns {*|boolean} true if the message should be displayed, false otherwise
 */
export const hasMessage = (metadata, propertyName) => {
    return metadata && metadata[propertyName] && metadata[propertyName].dirty && ! metadata[propertyName].valid && metadata[propertyName].message;
};

/**
 * Return true if all fields in the forms are valid and the form is dirty.
 *
 * @param metadata The metadata to be checked
 * @returns {boolean} true if form is submittable, false otherwise
 */
export const isFormSubmittable = (metadata) => {
    const keys = Object.keys(metadata);
    if (keys.length === 0){
        return false;
    }

    let valid = true;
    let dirty = false;

    for (let i = 0; i < keys.length; i++){
        const key = keys[i];
        const entry = metadata[key];
        valid = valid && entry.valid;
        dirty = dirty || entry.dirty;
    }

    // form should be submittable if each entry is valid and dirty.
    // if not dirty, it means that nothing has changed, so the form should not be submittable.
    return valid && dirty;
};

/**
 * Set state of a form based on the provided arguments.
 *
 * @param state The current state
 * @param args An array. Each element of the arguments must be in the form of {property: string, data: object, metadata: object}
 * @returns {{metadata: *, data: *}}
 */
export const setFormState = (state, args) => {
    const fullData = {...state.data};
    const fullMetadata = {...state.metadata};
    for (let i = 0; i < args.length; i++){
        const arg = args[i];
        if (Array.isArray(arg)){
            for (let j = 0; j < arg.length; j++){
                _updateFormState(arg[j], fullData, fullMetadata);
            }
        } else {
            _updateFormState(arg, fullData, fullMetadata);
        }
    }
    return {
        ...state,
        data: fullData,
        metadata: fullMetadata
    };
};

/**
 * Set up function to extract data.
 *
 * @param obj The object that exposes the extract data function
 * @param extractDataFunc The extract data function (optional)
 */
export const setupOnExtractData = (obj, extractDataFunc) => {
    const {props: {onExtractData}} = obj;
    if (onExtractData){
        if (extractDataFunc){
            // If the function to extract data is specified, then use from input
            onExtractData(extractDataFunc);
        } else {
            // Extract data function is not specified, default to obj.extractData
            onExtractData(obj.extractData);
        }
    }
};

const _updateFormState = (arg, fullData, fullMetadata) => {
    const {property, data, metadata} = arg;
    if (typeof (data) !== "undefined") {
        fullData[property] = data;
    }
    if (typeof (metadata) !== "undefined") {
        const newMetadata = fullMetadata[property] ? {...fullMetadata[property], ...metadata} : metadata;
        fullMetadata[property] = newMetadata;
    }
};
